import React, { useEffect, useState } from "react";
import { MapMarker, Video } from "../common/ObjectTypes";
import { toast } from "react-toastify";
import {
  createMapMarker,
  deleteMapMarker,
  getMapMarkerDetails,
  updateMapMarkerVideos,
  updateMapMarker,
} from "../../api/mapMarkers/mapMarkersApi";
import AddEditMapMarker from "./AddEditMapMarker";
import { useNavigate, useParams } from "react-router-dom";
import { getOrganizations } from "../../api/organizations/organizationsApi";
import { getCurrentUser } from "../../helpers/authHeader";

type ManageAddEditMapMarkerProps = {
  existingMapMarker: boolean;
  readOnly?: boolean;
  selectVideosPage?: boolean;
};

const ManageAddEditMapMarker: React.FC<ManageAddEditMapMarkerProps> = ({
  existingMapMarker,
  readOnly,
  selectVideosPage,
}) => {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const [pageLoaded, setPageLoaded] = useState(false);
  const { v4: uuidv4 } = require("uuid");
  const [addMapMarker, setAddMapMarker] = useState(false);
  const mapMarkerId = useParams<{ id: string }>().id;
  const [organizations, setOrganizations] = useState([]);
  const [changesMade, setChangesMade] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [mapMarker, setMapMarker] = useState<MapMarker>({
    name: "",
    mapMarkerId: "",
    description: "",
    latitude: 0,
    longitude: 0,
    iconID: 0,
    rowVersion: "",
    organizationId: "",
    videos: [],
  });

  useEffect(() => {
    const loadMapMarkerDetails = async () => {
      try {
        const temp = await getMapMarkerDetails(mapMarkerId ?? "");
        setMapMarker(temp);
        loadOrganizations();
      } catch (err) {
        console.log(err);
      }
    };

    const loadOrganizations = async () => {
      try {
        let temp;
        if (!currentUser.isSystemAdmin && !currentUser.isHeadsetAdmin) {
          temp = currentUser.organizations;
        } else {
          temp = await getOrganizations();
        }
        setOrganizations(temp);
      } catch (err) {
        console.log(err);
      }
    };

    if (existingMapMarker) loadMapMarkerDetails();
    loadOrganizations();
  }, [mapMarkerId, pageLoaded]);

  const handleOnClickAdd = () => {
    setAddMapMarker(true);
  };
  const handleSelectMapMarker = (mapMarker: MapMarker) => {
    setMapMarker({
      name: mapMarker.name,
      mapMarkerId: mapMarker.mapMarkerId,
      description: mapMarker.description,
      latitude: mapMarker.latitude,
      longitude: mapMarker.longitude,
      iconID: mapMarker.iconID,
      rowVersion: mapMarker.rowVersion,
      organizationId: mapMarker.organizationId,
      videos: mapMarker.videos,
    });
  };

  const handleChangeSingleSelect = (name: string, e: any) => {
    setMapMarker((prevMapMarker: any) => {
      const newValue = e === null ? null : e.value;
      const updatedMapMarker = {
        ...prevMapMarker,
        [name]: newValue,
      };
      return updatedMapMarker;
    });
    setChangesMade(true);
    setSaving(false);
  };

  const handleChange = (e: any) => {
    const { name, value, checked, type } = e.target;
    let newValue = type === "checkbox" ? checked : value;

    // Handle latitude and longitude by trimming spaces and converting to numbers
    if (
      (name === "latitude" || name === "longitude") &&
      typeof value === "string"
    ) {
      const trimmedValue = value.trim();

      // If it's empty or just a decimal point, keep it as is to allow user to continue typing
      if (
        trimmedValue === "" ||
        trimmedValue === "." ||
        trimmedValue === "-" ||
        trimmedValue === "-."
      ) {
        newValue = trimmedValue;
      } else {
        // If it's a number after trimming, convert to float
        const decimalNumberRegex = /^-?\d*(\.\d*)?$/;
        if (decimalNumberRegex.test(trimmedValue)) {
          // Allow partial numbers during typing
          newValue = trimmedValue;
        }
      }
    }

    setMapMarker((prevMapMarker) => {
      const updatedMapMarker = {
        ...prevMapMarker,
        [name]: newValue,
      };
      return updatedMapMarker;
    });
    setChangesMade(true);
    setSaving(false);
  };
  const getVideoIdsFromMapMarker = () => {
    try {
      const videosArray = mapMarker.videos || [];
      const videoIds = videosArray.map((video: any) => video.videoId);
      return videoIds;
    } catch (error) {
      console.error("Failed to parse video from localStorage", error);
      return [];
    }
  };

  const formIsValid = () => {
    const newErrors: any = {};
    const decimalNumberRegex = /^-?\d+(\.\d+)?$/;

    if (!mapMarker.name) newErrors.name = "Name is required.";
    if (!mapMarker.latitude) {
      newErrors.latitude = "Latitude is required.";
    } else if (!decimalNumberRegex.test(mapMarker.latitude.toString())) {
      newErrors.latitude = "Latitude must be a valid number.";
    }

    if (!mapMarker.longitude) {
      newErrors.longitude = "Longitude is required.";
    } else if (!decimalNumberRegex.test(mapMarker.longitude.toString())) {
      newErrors.longitude = "Longitude must be a valid number.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (e: any) => {
    e.preventDefault();
    setSaving(true);
    const videoIds = getVideoIdsFromMapMarker();
    const updatedMapMarker = {
      name: mapMarker.name,
      mapMarkerId: mapMarker.mapMarkerId || uuidv4(),
      iconId: mapMarker.iconID,
      videos: mapMarker.videos,
      description: mapMarker.description,
      latitude: mapMarker.latitude,
      longitude: mapMarker.longitude,
      organizationId: mapMarker.organizationId
        ? mapMarker.organizationId
        : currentUser.organizations.length === 1
        ? currentUser.organizations[0].organizationId
        : null,
      rowVersion: mapMarker.rowVersion,
    };
    try {
      let canSave = formIsValid();
      if (!canSave) {
        setSaving(false);
        return;
      } else if (!existingMapMarker) {
        await createMapMarker(updatedMapMarker);
        await updateMapMarkerVideos(updatedMapMarker.mapMarkerId, videoIds);

        toast.success(mapMarker.name + " added.", {
          position: toast.POSITION.TOP_RIGHT,
          hideProgressBar: true,
          autoClose: 2500,
        });
      } else {
        await updateMapMarker(updatedMapMarker);
        await updateMapMarkerVideos(updatedMapMarker.mapMarkerId, videoIds);

        toast.success(mapMarker.name + " updated.", {
          position: toast.POSITION.TOP_RIGHT,
          hideProgressBar: true,
          autoClose: 2500,
        });
      }
      navigate("/mapMarkers");
      setPageLoaded(false);
    } catch (error: any) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        setErrors({ onSave: error.response.data.message });
      } else {
        setErrors({ onSave: "An unexpected error occurred." });
      }
    }
  };

  const handleCancelEdit = () => {
    setMapMarker({
      name: "",
      mapMarkerId: "",
      description: "",
      latitude: 0,
      longitude: 0,
      iconID: 0,
      rowVersion: "",
      organizationId: "",
      videos: [],
    });
  };

  const handleCancelAdd = () => {
    setAddMapMarker(false);
  };

  const handleDelete = async () => {
    try {
      await deleteMapMarker(mapMarker.mapMarkerId ? mapMarker.mapMarkerId : "");
      setMapMarker({
        name: "",
        mapMarkerId: "",
        description: "",
        latitude: 0,
        longitude: 0,
        iconID: 0,
        rowVersion: "",
        organizationId: "",
        videos: [],
      });
      toast.success("MapMarker was deleted.", {
        position: toast.POSITION.TOP_RIGHT,
        hideProgressBar: true,
        autoClose: 2500,
      });
      setPageLoaded(false);
    } catch (error) {
      console.log(error);
    }
  };

  const handleSelectVideoForMapMarker = (
    selectedVideo: Video,
    isChecked: boolean
  ) => {
    setMapMarker((prevMapMarker: any) => {
      const currentVideos = prevMapMarker.videos || [];
      const videoObject = {
        videoId: selectedVideo.videoId,
        thumbnailUrl: selectedVideo.thumbnailUrl,
        name: selectedVideo.name,
        dateCreated: selectedVideo.dateCreated,
        lastEditedBy: selectedVideo.lastEditedBy,
        lastEditedDate: selectedVideo.lastEditedDate,
        overview: selectedVideo.overview,
        countryObtainedId: selectedVideo.countryObtainedId,
        species: selectedVideo.species || [],
        tags: selectedVideo.tags || [],
        mapMarkers: selectedVideo.mapMarkers || [],
        rowVersion: selectedVideo.rowVersion,
        streamingUrl: selectedVideo.streamingUrl,
        downloadUrl: selectedVideo.downloadUrl,
        totalViews: selectedVideo.totalViews,
        totalLikes: selectedVideo.totalLikes,
        totalDislikes: selectedVideo.totalDislikes,
        rating: selectedVideo.rating,
        youTubeUrl: selectedVideo.youTubeUrl,
        startTime: selectedVideo.startTime,
        endTime: selectedVideo.endTime,
        showInAllVideos: selectedVideo.showInAllVideos,
        compilation: selectedVideo.compilation,
        gameDrive: selectedVideo.gameDrive,
        graphicSex: selectedVideo.graphicSex,
        graphicDeath: selectedVideo.graphicDeath,
        videoFormat: selectedVideo.videoFormat,
        videoStatusId: selectedVideo.videoStatusId,
        //fix the resolution thing
        videoResolutionId: selectedVideo.videoResolutionId || 1,
      };

      const newVideos = isChecked
        ? currentVideos.some((v: any) => v.videoId === selectedVideo.videoId)
          ? currentVideos
          : [...currentVideos, videoObject]
        : currentVideos.filter((v: any) => v.videoId !== selectedVideo.videoId);
      const updatedMapMarker = {
        ...prevMapMarker,
        videos: newVideos, // Use validated videos here
      };

      return updatedMapMarker;
    });
    setChangesMade(true);
    setSaving(false);
  };

  return (
    <div className="flex justify-center">
      <AddEditMapMarker
        mapMarker={mapMarker}
        organizations={organizations}
        addMapMarker={addMapMarker}
        onClickAdd={handleOnClickAdd}
        onSelectMapMarker={handleSelectMapMarker}
        onChange={handleChange}
        onCancelEdit={handleCancelEdit}
        onCancelAdd={handleCancelAdd}
        onSave={handleSave}
        onUpdateMapMarker={handleSave}
        onDelete={handleDelete}
        onChangeSingleSelect={handleChangeSingleSelect}
        existingMapMarker={existingMapMarker}
        selectVideosPage={selectVideosPage}
        onSelectVideoForMapMarker={handleSelectVideoForMapMarker}
        changesMade={changesMade}
        saving={saving}
        errors={errors}
      />
    </div>
  );
};

export default ManageAddEditMapMarker;
